import { createRoute, Link } from '@tanstack/react-router'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MutedText,
  Stack,
} from '@volee/ui'

import { MfaForm, NewPasswordForm, SignInForm } from '../components/sign-in-form.tsx'
import { useSignIn } from '../data/use-sign-in.ts'
import { messages } from '../messages/index.ts'
import { parseOptionalOneOf, parseReturnTo } from './params.ts'
import { rootRoute } from './root.tsx'

// The public sign-in route: email and password against Cognito, no hosted UI. The first access is a
// STATE of this page, not a route: apps/admin-panel/docs/login-sta-dentro-il-prodotto.md

// The values first, the type derived from them, as `ROLES`/`Role` does in @volee/api-client.
const SIGN_IN_REASONS = ['session-expired', 'required'] as const

export type SignInReason = (typeof SIGN_IN_REASONS)[number]

export interface SignInSearch {
  readonly returnTo: string
  readonly reason?: SignInReason
}

/** A whitelist, because the value selects a sentence shown on a Volée-branded page. */
export function parseReason(value: unknown): SignInReason | undefined {
  return parseOptionalOneOf(SIGN_IN_REASONS, value)
}

/** The login as a link target, for the surfaces that have to offer it. */
export const signInLink = { to: '/sign-in', search: { returnTo: '/' } } as const

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    returnTo: parseReturnTo(search.returnTo),
    reason: parseReason(search.reason),
  }),
  // Reached without the back office being usable: no frame around it (see `chrome`).
  staticData: { chrome: 'bare' },
  component: SignIn,
})

// Annotated here: this is the line that stops compiling when a reason gains no sentence.
const REASON_TEXT: Record<SignInReason, string> = messages.signIn.reason

function SignIn() {
  const { returnTo, reason } = signInRoute.useSearch()
  const { pending, credentials, newPassword, mfa } = useSignIn(returnTo)

  // `info`, not `destructive`: an expired session is not the user's mistake.
  function reasonNotice() {
    if (reason === undefined || pending !== null) return null
    return (
      <Alert variant="info">
        <AlertDescription>{REASON_TEXT[reason]}</AlertDescription>
      </Alert>
    )
  }

  function heading() {
    if (pending?.kind === 'new-password-required') {
      return { title: messages.newPassword.title, description: messages.newPassword.description }
    }
    if (pending?.kind === 'mfa-required') {
      return { title: messages.mfa.title, description: messages.mfa.description }
    }
    return { title: messages.signIn.title, description: messages.signIn.description }
  }

  function body() {
    if (pending?.kind === 'new-password-required') return <NewPasswordForm {...newPassword} />
    if (pending?.kind === 'mfa-required') return <MfaForm {...mfa} />
    return (
      <Stack gap="md">
        <SignInForm {...credentials} />
        <Button variant="ghost" asChild>
          {/* The literal, not the route object: /forgot-password links back here. */}
          <Link to="/forgot-password" search={{ returnTo }}>
            {messages.signIn.forgotPassword}
          </Link>
        </Button>
      </Stack>
    )
  }

  const { title, description } = heading()

  return (
    <section className="mx-auto flex max-w-sm flex-col justify-center py-10">
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <MutedText>{description}</MutedText>
        </CardHeader>
        <CardContent>
          <Stack gap="md">
            {reasonNotice()}
            {body()}
          </Stack>
        </CardContent>
      </Card>
    </section>
  )
}
